import React from 'react';
import { Select, Card, Typography, Space, Tag } from 'antd';
import { BankOutlined, DollarOutlined, CalendarOutlined } from '@ant-design/icons';
import { useAuthStore, selectableBusinessUnits } from '@/store/authStore';

const { Title, Text } = Typography;

interface BusinessUnitSelectorProps {
  style?: React.CSSProperties;
  size?: 'small' | 'middle' | 'large';
  showDetails?: boolean;
}

const BusinessUnitSelector: React.FC<BusinessUnitSelectorProps> = ({ 
  style, 
  size = 'middle',
  showDetails = false 
}) => {
  const { 
    authorization,
    currentBusinessUnit,
    setCurrentBusinessUnit
  } = useAuthStore();
  const availableUnits = selectableBusinessUnits(authorization);

  const handleChange = (businessUnitId: string) => {
    setCurrentBusinessUnit(businessUnitId);
  };

  const formatRetentionDays = (days: number): string => {
    const years = Math.floor(days / 365);
    const remainingDays = days % 365;
    if (years > 0) {
      return remainingDays > 0 ? `${years}y ${remainingDays}d` : `${years}y`;
    }
    return `${days}d`;
  };

  if (showDetails && currentBusinessUnit) {
    return (
      <Card 
        size="small" 
        style={{ 
          ...style,
          background: 'linear-gradient(135deg, #f0f8ff 0%, #e6f7ff 100%)',
          border: '1px solid #91d5ff'
        }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space>
              <BankOutlined style={{ color: '#1890ff' }} />
              <Title level={5} style={{ margin: 0 }}>
                {currentBusinessUnit.name}
              </Title>
            </Space>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <Space>
              <DollarOutlined style={{ color: '#52c41a' }} />
              <Text type="secondary">{currentBusinessUnit.costCenter}</Text>
            </Space>
            <Space>
              <CalendarOutlined style={{ color: '#faad14' }} />
              <Text type="secondary">
                {formatRetentionDays(currentBusinessUnit.retentionDays)} retention
              </Text>
            </Space>
          </div>
        </Space>
      </Card>
    );
  }

  // If user has only one business unit, just display it as a tag (no dropdown)
  if (availableUnits.length === 1 && currentBusinessUnit) {
    return (
      <Tag 
        icon={<BankOutlined />} 
        color="blue"
        style={{ margin: 0, ...style }}
      >
        {currentBusinessUnit.name}
      </Tag>
    );
  }

  // If user has multiple business units, show dropdown
  if (availableUnits.length > 1) {
    return (
      <Select
        value={currentBusinessUnit?.id}
        onChange={handleChange}
        style={{ minWidth: 200, ...style }}
        size={size}
        placeholder="Select Business Unit"
        optionLabelProp="label"
      >
        {availableUnits.map((businessUnit) => (
          <Select.Option 
            key={businessUnit.id} 
            value={businessUnit.id}
            label={businessUnit.name}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Space>
                <BankOutlined />
                <span>{businessUnit.name}</span>
              </Space>
            </div>
          </Select.Option>
        ))}
      </Select>
    );
  }

  // Fallback: no business units available
  return null;
};

export default BusinessUnitSelector;
